import { SxProps } from "@mui/material";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import uniqueId from "lodash.uniqueid";
import cloneDeep from "lodash/cloneDeep";
import React, { Component, SyntheticEvent } from "react";
import { FileUpload } from "./FileUpload";

interface MultiFileUploadProps {
  files?: File[] | null;
  onChange?: (e: SyntheticEvent, files: File[]) => void;
  id?: string;
  accept?: string;
  disabled?: boolean;
  label?: string;
  placeholder?: string;
  InputLabelProps?: any;
  allowedMimeTypes?: string[];
  allowedExtensions?: string[];
  maxFileSize?: number;
  errorMimeType?: string;
  errorFileSize?: string;
  helperText?: string;
  onError?: (error: string | null) => void;
  multiple?: boolean;
  sx?: SxProps;
}

interface MultiFileUploadState {
  files?: File[] | null;
}

export class MultiFileUpload extends Component<MultiFileUploadProps, MultiFileUploadState> {
  static defaultProps = {
    multiple: true
  };

  currentFileUploadRef: React.RefObject<any>;

  constructor(props: MultiFileUploadProps) {
    super(props);

    this.state = {
      files: cloneDeep(props.files || [])
    };

    this.addBlank();
    this.currentFileUploadRef = React.createRef();
  }

  onFileChange(e: React.MouseEvent, key: string, file: File, multiple: boolean) {
    const { files } = this.state;

    if (!files) return;

    files[key as unknown as number] = file;
    if (!multiple) {
      this.addBlank();
      this.setState({ files }, () => this.fireOnChange(e, files));
    }
  }

  onMultipleFilesSelected(e: React.MouseEvent, newFiles: File[]) {
    const { files } = this.state;

    newFiles.map((file) => this.addFile(file));
    this.addBlank();
    this.setState({ files }, () => this.fireOnChange(e, files));
  }

  fireOnChange(e: SyntheticEvent, files?: File[] | null) {
    const { onChange } = this.props;

    const newFiles: any = [];
    Object.values(files || [])
      .filter((f) => f !== null)
      .forEach((f, index) => (newFiles["file_" + index] = f));
    if (onChange) onChange(e, newFiles);
  }

  onRemoveFile(e: SyntheticEvent, key: string, file?: File) {
    const { files } = this.state;

    const newFiles: any = [];
    const otherFiles = Object.entries(files || []).filter(([k, f]) => k !== key);
    otherFiles.forEach(([k, f], index) => (newFiles["file_" + index] = f));

    this.setState({ files: newFiles }, () => this.fireOnChange(e, newFiles));
  }

  addBlank() {
    const { files } = this.state;
    const fileEntries = Object.entries(files || []);
    const hasEmpty = fileEntries.some((i) => !i[1]);
    if (hasEmpty) return;
    this.addFile(null);
    //files['file_' + fileEntries.length] = null;
  }

  addFile(file: any) {
    const { files } = this.state;
    const fileEntries = Object.entries(files || []);
    if (!files) return;
    files[("file_" + fileEntries.length) as unknown as number] = file;
  }

  selectFile(e: React.MouseEvent) {
    this.currentFileUploadRef.current?.selectFile(e);
  }

  dropFile(e: React.DragEvent) {
    this.currentFileUploadRef.current?.dropFile(e);
  }

  render() {
    const {
      id,
      accept,
      disabled,
      label,
      placeholder,
      InputLabelProps,
      allowedMimeTypes,
      allowedExtensions,
      maxFileSize,
      errorMimeType,
      errorFileSize,
      helperText,
      onError,
      multiple,
      sx
    } = this.props;
    const { files } = this.state;

    const fileEntries = Object.entries(files || []);

    return (
      <Stack spacing={2} sx={sx}>
        {fileEntries.map(([key, file], i) => (
          <Grid key={key} item xs={12} md={12} lg={12}>
            <FileUpload
              id={uniqueId(id + "_")}
              ref={i === fileEntries.length - 1 ? this.currentFileUploadRef : null}
              className={i === fileEntries.length - 1 ? "klayo-multifileupload_current" : null}
              label={label}
              disabled={disabled}
              placeholder={placeholder}
              accept={accept}
              onFileChange={(e, file, multiple) => this.onFileChange(e, key, file, multiple)}
              onMultipleFilesSelected={this.onMultipleFilesSelected.bind(this)}
              onError={onError}
              hasRemove={Object.values(this.state.files || []).length > 1}
              file={file}
              allowedMimeTypes={allowedMimeTypes}
              maxFileSize={maxFileSize}
              allowedExtensions={allowedExtensions}
              multiple={multiple}
              errorMimeType={errorMimeType}
              helperText={helperText}
              errorFileSize={errorFileSize}
              onRemoveFile={(e, file) => this.onRemoveFile(e, key, file)}
              sx={{ width: "100%" }}
            />
          </Grid>
        ))}
      </Stack>
    );
  }
}
